import React from 'react';
import { useAuth } from '../lib/auth';
import { Calendar, Mail, ExternalLink, ArrowRight } from 'lucide-react';

function googleLogin() {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function GoogleCalendar() {
  const { user } = useAuth();
  const isGoogleUser = user?.auth_provider === 'google' || user?.email?.endsWith('@gmail.com');

  return (
    <div className="tm-board-bg flex flex-col p-6 min-h-[calc(100vh-48px)]">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Calendar className="text-blue-400" size={24} />
            Google Meeting Calendar
          </h1>
          <p className="text-slate-300 text-sm mt-1">
            Manage your schedule and join Google Meet video sessions directly from BexConnect.
          </p>
        </div>
      </div>

      {isGoogleUser ? (
        /* Google Calendar Iframe Wrapper */
        <div className="flex-1 bg-white/95 backdrop-blur-md rounded-xl border border-slate-200/50 shadow-2xl overflow-hidden flex flex-col min-h-[500px]">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></span>
              Synchronized with <span className="text-blue-600 font-semibold">{user?.email}</span>
            </div>
            <a
              href="https://calendar.google.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 transition-colors"
            >
              Open in Google <ExternalLink size={12} />
            </a>
          </div>
          <iframe
            src={`https://calendar.google.com/calendar/embed?src=primary&src=${encodeURIComponent(user?.email || '')}&ctz=Asia%2FKolkata`}
            style={{ border: 0, width: '100%', height: '100%', flex: 1 }}
            frameBorder="0"
            scrolling="no"
            title="Google Calendar Embed"
          />

          {/* Troubleshooting Guidelines Banner */}
          <div className="bg-slate-900/10 border-t border-slate-200/50 p-4 text-xs text-slate-600 flex flex-col gap-2">
            <p className="font-semibold text-slate-800 flex items-center gap-1.5">
              💡 Events not showing up?
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-700 pl-1">
              <li>
                <strong>Browser Permissions</strong>: Modern browsers block iframe tracking by default. Check your browser address bar for a <strong>cookie/shield icon</strong> and select <strong>"Allow cookies / third-party content"</strong> for <code>calendar.google.com</code>.
              </li>
              <li>
                <strong>Calendar Access Settings</strong>: Go to your <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium underline">Google Calendar Settings</a>, select <strong>{user?.email}</strong> under <em>"Settings for my calendars"</em>, go to <em>"Access permissions for events"</em>, and verify that <strong>"Make available to public"</strong> is checked (or share it with your account).
              </li>
            </ul>
          </div>
        </div>
      ) : (
        /* Call to Action for non-Google accounts */
        <div className="flex-1 flex items-center justify-center py-12">
          <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 shadow-2xl text-center text-white relative overflow-hidden group">
            {/* Ambient Background Glow */}
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-blue-500/20 rounded-full blur-3xl group-hover:bg-blue-500/35 transition-all duration-500" />
            <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-indigo-500/20 rounded-full blur-3xl group-hover:bg-indigo-500/35 transition-all duration-500" />

            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/10 shadow-lg">
              <Calendar className="text-blue-300" size={32} />
            </div>

            <h2 className="text-xl font-bold mb-2">Connect Google Calendar</h2>
            <p className="text-slate-200 text-sm mb-6 leading-relaxed">
              Login to your BexConnect workspace using your Google account to automatically sync your calendar meetings and schedule Google Meet sessions.
            </p>

            <button
              onClick={googleLogin}
              className="w-full bg-white text-slate-900 hover:bg-slate-50 font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-150 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.85z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.85c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign in with Google
            </button>

            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-300">
              <Mail size={12} className="text-slate-400" />
              <span>Works directly with Google Workspace</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
